import { useState, type ReactNode } from 'react';

import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ChipProps } from '@mui/material/Chip';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { useConfirm } from '@erikdakoda/confirm-ui/useConfirm';
import { useTextInputDialog } from '@erikdakoda/confirm-ui/useTextInputDialog';
import { formatDateISO } from '@erikdakoda/i18n/shared/dateFormat';
import {
  buildConnectableAuthSchemes,
  COMPOSIO_MANAGED_OAUTH_SCHEMES,
} from '@erikdakoda/connection/shared/connectableAuthSchemes';
import type { ListedToolkit } from '@erikdakoda/connection/shared/toolkitCatalogTypes';
import type { ConnectionForCard } from '@erikdakoda/connection/types';
import type { ConnectionStatus, ToolAccessMode } from '@erikdakoda/database/enums';
import type { MetricConnectionForCard } from '../shared/metricConnectorTypes';
import type { PinnedConnector } from '@erikdakoda/site-template-ui/types';
import type { ToolAccessModeSuccess } from '@erikdakoda/connection/api-handlers/ConnectionToolAccessModeHandler';
import type { EnableSuccess } from '@erikdakoda/connection/api-handlers/ConnectionEnableHandler';
import type { RefreshSuccess } from '@erikdakoda/connection/api-handlers/ConnectionRefreshHandler';
import type { RenameSuccess } from '@erikdakoda/connection/api-handlers/ConnectionRenameHandler';
import type { SetDefaultSuccess } from '@erikdakoda/connection/api-handlers/ConnectionSetDefaultHandler';
import type { DeleteSuccess } from '@erikdakoda/connection/api-handlers/ConnectionDeleteHandler';
import DropdownMenu from '@erikdakoda/ui/components/DropdownMenu';
import FaIconUvilo from '@erikdakoda/ui/components/FaIconUvilo';
import type { MenuItemType } from '@erikdakoda/ui/components/MenuItems';
import PowerIconButton from '@erikdakoda/ui/components/PowerIconButton';
import Spinner from '@erikdakoda/ui/components/Spinner';
import { doFetch } from '@erikdakoda/ui/shared/doFetch';
import { useException } from '@erikdakoda/ui/hooks/useException';
import { useSnack } from '@erikdakoda/ui/hooks/useSnack';

import ConnectionStatusChip from './ConnectionStatusChip';
import ToolkitLogo from './ToolkitLogo';

const STATUSES_NEEDING_RECONNECT = new Set(['EXPIRED', 'FAILED', 'REVOKED', 'INACTIVE']);

/**
 * One connection or catalog toolkit card in the Composio-inspired grid layout.
 * Connection variant: manage rename / reconnect / disconnect / set-default.
 * Toolkit variant: selectable catalog entry with tool count and auth scheme pills.
 * Never renders `connectedAccountId`, `connectStateNonce`, or any Composio session id.
 * @param props Discriminated by `variant` — connection management vs toolkit pick.
 * @returns One connection card.
 */
export default function ConnectionCard(props: ConnectionCardProps) {
  'use memo';

  if (props.variant === 'toolkit') {
    return (
      <ToolkitConnectionCard {...props} className={clsx('connection-card', props.className)} />
    );
  }
  if (props.variant === 'metricConnector') {
    return (
      <MetricConnectorConnectionCard
        {...props}
        className={clsx('connection-card', props.className)}
      />
    );
  }
  return <ManagedConnectionCard {...props} className={clsx('connection-card', props.className)} />;
}

/** Icon-only chip for connection tool access (pencil = read+write, pencil-slash = read only). */
function ToolAccessModeChip({
  toolAccessMode,
}: {
  readonly toolAccessMode: ToolAccessMode;
}): ReactNode {
  'use memo';

  const { t } = useTranslation('connection-ui');
  const isReadWrite = toolAccessMode === 'READ_WRITE';

  return (
    <Tooltip
      className="tool-access-mode-chip"
      title={t(
        isReadWrite ? 'toolAccessModeChoiceHintReadWrite' : 'toolAccessModeChoiceHintReadOnly',
      )}
    >
      <Chip
        className="icon-chip"
        size="small"
        variant="outlined"
        color={isReadWrite ? 'warning' : 'default'}
        aria-label={t(isReadWrite ? 'toolAccessModeReadWrite' : 'toolAccessModeReadOnly')}
        label={<FaIconUvilo icon={isReadWrite ? 'pencil' : 'pencil-slash'} size="xs" square />}
      />
    </Tooltip>
  );
}

/**
 * Best-effort title when `toolkitName` was never stored (legacy rows).
 * @param slug Composio toolkit slug (usually lowercase).
 * @returns Title-cased words from the slug.
 */
function humanizeToolkitSlug(slug: string): string {
  return slug
    .split(/[-_]+/)
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

type ConnectionCardConnectionProps = {
  readonly variant?: 'connection';
  readonly connection: ConnectionForCard;
  readonly onChanged: () => void;
  readonly onReconnect: (connection: ConnectionForCard) => void;
  /** When false, hides the Default account chip (only one connection for this toolkit). */
  readonly showDefaultBadge?: boolean;
  readonly className?: string;
};

type ConnectionCardToolkitProps = {
  readonly variant: 'toolkit';
  readonly toolkit: ListedToolkit;
  readonly connectedCount?: number;
  readonly disabled?: boolean;
  readonly showToolkitStats?: boolean;
  readonly onSelect: () => void;
  readonly className?: string;
};

type ConnectionCardMetricConnectorProps = {
  readonly variant: 'metricConnector';
  readonly pin: PinnedConnector;
  readonly connection: MetricConnectionForCard | null;
  /** Auth scheme ids for the bottom chip row (`OAUTH2` for cloud, `DEVICE` for OS). */
  readonly authSchemes: readonly string[];
  readonly description?: string | null;
  readonly connectEnabled: boolean;
  readonly connectDisabledReason?: string;
  readonly onConnect: () => void | Promise<void>;
  readonly onDisconnect: (connectionId: string) => void | Promise<void>;
  readonly onDelete: (connectionId: string) => void | Promise<void>;
  readonly onResync?: (connectionId: string) => void | Promise<void>;
  readonly resyncMenuTitle?: string;
  readonly resyncMenuIcon?: string;
  readonly renderConnectionMeta?: (connection: MetricConnectionForCard) => ReactNode;
  readonly isLoading?: boolean;
  readonly className?: string;
};

export type ConnectionCardProps =
  | ConnectionCardConnectionProps
  | ConnectionCardToolkitProps
  | ConnectionCardMetricConnectorProps;

type AuthSchemeBadge = {
  readonly label: string;
  readonly color: ChipProps['color'];
};

/**
 * Maps an auth scheme enum to a short pill label and chip color. Covers the Composio schemes and
 * the `DEVICE` scheme metric OS connectors report.
 * @param authScheme Uppercase scheme id (e.g. `OAUTH2`, `API_KEY`, `DEVICE`).
 * @returns Display label and MUI chip color.
 */
function authSchemeBadge(authScheme: string): AuthSchemeBadge {
  const upper = authScheme.toUpperCase();
  if (COMPOSIO_MANAGED_OAUTH_SCHEMES.has(upper)) {
    return { label: upper === 'OAUTH1' ? 'OAuth1' : 'OAuth2', color: 'info' };
  }
  if (upper === 'DEVICE') {
    return { label: 'Device', color: 'secondary' };
  }
  if (upper === 'API_KEY') {
    return { label: 'API Key', color: 'success' };
  }
  if (upper === 'BEARER_TOKEN') {
    return { label: 'Bearer', color: 'success' };
  }
  if (upper === 'BASIC' || upper === 'BASIC_WITH_JWT') {
    return { label: 'Basic', color: 'default' };
  }
  if (upper === 'NO_AUTH') {
    return { label: 'No Auth', color: 'default' };
  }
  return { label: upper.replaceAll('_', ' '), color: 'default' };
}

/**
 * Shared Composio-inspired card chrome: centered logo/title/body, top-right header action.
 */
function ConnectionCardBody({
  toolkitSlug,
  title,
  subtitle,
  description,
  connectedCount = 0,
  stats,
  meta,
  authSchemes,
  toolAccessMode,
  footer,
  headerAction,
  logoNode,
}: {
  readonly toolkitSlug: string;
  readonly title: string;
  readonly subtitle?: string | null;
  readonly description?: string | null;
  readonly connectedCount?: number;
  readonly stats: ReactNode;
  readonly meta?: ReactNode;
  readonly authSchemes: readonly string[];
  readonly toolAccessMode?: ToolAccessMode | null;
  readonly footer?: ReactNode;
  readonly headerAction?: ReactNode;
  readonly logoNode?: ReactNode;
}): ReactNode {
  'use memo';

  const { t } = useTranslation('connection-ui');
  const logo = logoNode ?? <ToolkitLogo toolkitSlug={toolkitSlug} size={56} alt="" />;
  const showBottomChips = authSchemes.length > 0 || toolAccessMode != null;

  return (
    <CardContent
      className="connection-card-body"
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        flex: 1,
        height: '100%',
        p: 4,
        textAlign: 'center',
        '&:last-child': { pb: 4 },
      }}
    >
      {headerAction && (
        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>{headerAction}</Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        {connectedCount > 0 ? (
          <Tooltip title={t('alreadyConnectedCount', { count: connectedCount })}>
            <Badge
              badgeContent={connectedCount}
              color="error"
              overlap="circular"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.7rem',
                  bgcolor: 'error.main',
                },
              }}
            >
              {logo}
            </Badge>
          </Tooltip>
        ) : (
          logo
        )}
      </Box>

      <Box sx={{ width: '100%', mb: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.5,
              overflowWrap: 'anywhere',
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      {description && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            width: '100%',
            my: 2,
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.4,
            minHeight: '4.2em',
          }}
        >
          {description}
        </Typography>
      )}

      {/* Stretch filler so chips sit at the card bottom; spare height lives under the name. */}
      <Box sx={{ flexGrow: 1 }} />

      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        sx={{
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        {stats}
      </Stack>

      {meta ? <Box sx={{ width: '100%', mt: 2 }}>{meta}</Box> : null}

      {showBottomChips && (
        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          sx={{ flexWrap: 'wrap', justifyContent: 'center', width: '100%', mt: 2 }}
        >
          {authSchemes.map((scheme) => {
            const badge = authSchemeBadge(scheme);
            return (
              <Chip
                key={scheme}
                size="small"
                label={badge.label}
                color={badge.color}
                variant="outlined"
              />
            );
          })}
          {toolAccessMode != null ? <ToolAccessModeChip toolAccessMode={toolAccessMode} /> : null}
        </Stack>
      )}

      {footer ? <Box sx={{ width: '100%' }}>{footer}</Box> : null}
    </CardContent>
  );
}

function ToolkitConnectionCard({
  toolkit,
  connectedCount = 0,
  disabled = false,
  showToolkitStats = true,
  onSelect,
  className,
}: ConnectionCardToolkitProps) {
  'use memo';

  const { t } = useTranslation('connection-ui');
  const connectableSchemes = buildConnectableAuthSchemes(
    toolkit.authSchemes,
    toolkit.composioManagedAuthSchemes,
  );
  const authSchemes = connectableSchemes.map((scheme) => scheme.authScheme);
  const updatedLabel = toolkit.updatedAt ? formatDateISO(toolkit.updatedAt) : '';
  const versionLabel = toolkit.version
    ? t('toolkitVersion', {
        version: toolkit.version.startsWith('v') ? toolkit.version.slice(1) : toolkit.version,
      })
    : null;

  return (
    <Card
      className={clsx('toolkit-connection-card', className)}
      variant="outlined"
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <ConnectionCardBody
        toolkitSlug={toolkit.slug}
        title={toolkit.displayName}
        subtitle={disabled && connectedCount > 0 ? t('maxAccountsReached') : null}
        description={toolkit.description || null}
        connectedCount={connectedCount}
        headerAction={
          <PowerIconButton
            icon="plus"
            size="medium"
            padding={3}
            disabled={disabled}
            onClick={onSelect}
            title={t('addConnection')}
            aria-label={t('addConnection')}
          />
        }
        stats={
          showToolkitStats ? (
            <Stack
              direction="row"
              spacing={0}
              useFlexGap
              sx={{ flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center' }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <FaIconUvilo icon="hammer" size="xs" color="text.secondary" />
                <Typography variant="caption" color="text.secondary">
                  {t('toolkitToolCount', { count: toolkit.toolsCount })}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ px: 1.5 }}>
                ·
              </Typography>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <FaIconUvilo icon="bolt" size="xs" color="text.secondary" />
                <Typography variant="caption" color="text.secondary">
                  {t('toolkitTriggerCount', { count: toolkit.triggersCount })}
                </Typography>
              </Stack>
            </Stack>
          ) : null
        }
        meta={
          versionLabel || updatedLabel ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {versionLabel}
              {versionLabel && updatedLabel ? (
                <Box component="span" sx={{ px: 1.5 }}>
                  ·
                </Box>
              ) : null}
              {updatedLabel ? t('toolkitUpdatedAt', { date: updatedLabel }) : null}
            </Typography>
          ) : null
        }
        authSchemes={authSchemes}
      />
    </Card>
  );
}

function ManagedConnectionCard({
  connection,
  onChanged,
  onReconnect,
  showDefaultBadge = true,
  className,
}: ConnectionCardConnectionProps) {
  'use memo';

  const { t } = useTranslation('connection-ui');
  const { snack } = useSnack();
  const exception = useException();
  const confirm = useConfirm();
  const getTextInputDialog = useTextInputDialog();
  const [isLoading, setIsLoading] = useState(false);

  const title =
    connection.toolkitName?.trim() ||
    humanizeToolkitSlug(connection.toolkitSlug) ||
    connection.toolkitSlug;
  const subtitle = connection.name.trim() !== '' ? connection.name : null;
  const needsReconnect = STATUSES_NEEDING_RECONNECT.has(connection.status);
  const authSchemes = connection.authScheme ? [connection.authScheme] : [];

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      await doFetch<RefreshSuccess>({
        rowId: connection.id,
        url: `/api/connections/${connection.id}/refresh`,
        method: 'POST',
        params: {},
        snack,
        exception,
      });
      onChanged();
    } finally {
      setIsLoading(false);
    }
  };

  const handleRename = async () => {
    const result = await getTextInputDialog<{ name: string }>({
      inputs: [{ name: 'name', label: t('rename'), value: connection.name ?? '' }],
      dialogBoxProps: {
        title: t('rename'),
      },
    });
    if (!result || !result.name.trim()) return;

    setIsLoading(true);
    try {
      const renamed = await doFetch<RenameSuccess | undefined>({
        rowId: connection.id,
        url: `/api/connections/${connection.id}/rename`,
        method: 'POST',
        params: { name: result.name.trim() },
        snack,
        exception,
      });
      // The rename handler returns no success message, so the card shows its own.
      if (renamed?.connectionId) {
        snack.success(t('connectionRenamed'));
      }
      onChanged();
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnable = async () => {
    setIsLoading(true);
    try {
      await doFetch<EnableSuccess>({
        rowId: connection.id,
        url: `/api/connections/${connection.id}/enable`,
        method: 'POST',
        params: {},
        snack,
        exception,
      });
      onChanged();
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccessModeChange = async (): Promise<void> => {
    const toolAccessMode =
      connection.toolAccessMode === 'READ_ONLY' ? ('READ_WRITE' as const) : ('READ_ONLY' as const);
    setIsLoading(true);
    try {
      await doFetch<ToolAccessModeSuccess>({
        rowId: connection.id,
        url: `/api/connections/${connection.id}/access-mode`,
        method: 'POST',
        params: { toolAccessMode },
        snack,
        exception,
      });
      onChanged();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetDefault = async () => {
    setIsLoading(true);
    try {
      await doFetch<SetDefaultSuccess>({
        rowId: connection.id,
        url: `/api/connections/${connection.id}/set-default`,
        method: 'POST',
        params: {},
        snack,
        exception,
      });
      onChanged();
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelPending = async () => {
    setIsLoading(true);
    try {
      await doFetch<DeleteSuccess>({
        rowId: connection.id,
        url: `/api/connections/${connection.id}`,
        method: 'DELETE',
        params: {},
        snack,
        exception,
      });
      onChanged();
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    const { confirmed } = await confirm({
      title: t('disconnect'),
      description: t('disconnectConfirmDescription', { name: title }),
    });
    if (!confirmed) return;

    setIsLoading(true);
    try {
      await doFetch<DeleteSuccess>({
        rowId: connection.id,
        url: `/api/connections/${connection.id}`,
        method: 'DELETE',
        params: {},
        snack,
        exception,
      });
      onChanged();
    } finally {
      setIsLoading(false);
    }
  };

  const menuItems: MenuItemType[] = [
    {
      title: t('rename'),
      icon: 'pen-to-square',
      value: 'rename',
      onClick: handleRename,
    },
    {
      title:
        connection.toolAccessMode === 'READ_ONLY' ? t('enableReadWrite') : t('switchToReadOnly'),
      icon: 'shield-halved',
      value: 'access-mode',
      onClick: handleAccessModeChange,
    },
    {
      title: t('reconnect'),
      icon: 'rotate',
      value: 'reconnect',
      onClick: () => onReconnect(connection),
    },
    // INACTIVE means Composio disabled the account but kept credentials — enabling avoids re-auth.
    ...(connection.status === 'INACTIVE'
      ? [
          {
            title: t('enable'),
            icon: 'play',
            value: 'enable',
            onClick: handleEnable,
          } as MenuItemType,
        ]
      : []),
    ...(connection.isDefault
      ? []
      : [
          {
            title: t('setAsDefault'),
            icon: 'star',
            value: 'set-default',
            onClick: handleSetDefault,
          } as MenuItemType,
        ]),
    {
      title: t('disconnect'),
      icon: 'light-puzzle-piece-slash',
      value: 'disconnect',
      onClick: handleDisconnect,
    },
  ];

  return (
    <Card
      className={clsx('managed-connection-card', className)}
      variant="outlined"
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      <ConnectionCardBody
        toolkitSlug={connection.toolkitSlug}
        title={title}
        subtitle={subtitle}
        headerAction={
          <DropdownMenu
            className={`connection-card-${connection.id}-dropdown`}
            icon="ellipsis-stroke-vertical"
            title={t('connectionActions')}
            items={menuItems}
            position="left"
            size="medium"
            padding={3}
            loading={isLoading}
          />
        }
        stats={
          <>
            <ConnectionStatusChip status={connection.status} />
            {connection.scope === 'SPACE' ? (
              <Chip size="small" label={t('spaceBadge')} variant="outlined" />
            ) : connection.scope === 'USER' ? (
              <Chip size="small" label={t('personalBadge')} variant="outlined" />
            ) : connection.scope === 'SERVICE' ? (
              <Chip size="small" label={t('serviceBadge')} variant="outlined" />
            ) : null}
            {showDefaultBadge && connection.isDefault && (
              <Chip
                size="small"
                label={t('defaultAccountBadge')}
                variant="outlined"
                color="primary"
              />
            )}
          </>
        }
        meta={
          connection.connectedAt || connection.lastVerifiedAt ? (
            <Stack spacing={0.5} sx={{ width: '100%', alignItems: 'center' }}>
              {connection.connectedAt ? (
                <Typography variant="caption" color="text.secondary">
                  {t('connectedAt', { date: formatDateISO(connection.connectedAt) })}
                </Typography>
              ) : null}
              {connection.lastVerifiedAt ? (
                <Typography variant="caption" color="text.secondary">
                  {t('verifiedAt', { date: formatDateISO(connection.lastVerifiedAt) })}
                </Typography>
              ) : null}
            </Stack>
          ) : null
        }
        authSchemes={authSchemes}
        toolAccessMode={connection.toolAccessMode}
        footer={
          connection.status === 'INITIATED' ||
          (connection.statusReason && needsReconnect) ||
          needsReconnect ? (
            <>
              {connection.status === 'INITIATED' && (
                <Stack spacing={2} sx={{ alignItems: 'center' }}>
                  {/* The status chip already reads "Waiting for authorization" — this adds the next step. */}
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{ alignItems: 'center', justifyContent: 'center' }}
                  >
                    <Spinner width={16} />
                    <Typography variant="body2" color="text.secondary">
                      {t('finishInOtherWindow')}
                    </Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    sx={{ flexWrap: 'wrap', justifyContent: 'center' }}
                  >
                    <Button size="small" onClick={handleRefresh} loading={isLoading}>
                      {t('checkNow')}
                    </Button>
                    <Button
                      size="small"
                      color="inherit"
                      onClick={handleCancelPending}
                      disabled={isLoading}
                    >
                      {t('cancelPending')}
                    </Button>
                  </Stack>
                </Stack>
              )}

              {connection.statusReason && needsReconnect && (
                <Typography variant="body2" color="text.secondary">
                  {connection.statusReason}
                </Typography>
              )}

              {needsReconnect && (
                <Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onReconnect(connection)}
                    disabled={isLoading}
                  >
                    {t('reconnect')}
                  </Button>
                </Box>
              )}
            </>
          ) : undefined
        }
      />
    </Card>
  );
}

function MetricConnectorLogo({
  pin,
  size,
}: {
  readonly pin: PinnedConnector;
  readonly size: number;
}): ReactNode {
  'use memo';

  if (pin.logo.trim() !== '') {
    return (
      <Box
        className="metric-connector-logo"
        component="img"
        src={pin.logo}
        alt=""
        sx={{ width: size, height: size, objectFit: 'contain', borderRadius: 1 }}
      />
    );
  }

  const icon = pin.kind === 'os' ? 'mobile' : pin.kind === 'cloud' ? 'cloud' : 'link';

  return (
    <Box
      className="metric-connector-logo"
      sx={{
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        bgcolor: 'action.hover',
      }}
    >
      <FaIconUvilo icon={icon} size="lg" color="text.secondary" />
    </Box>
  );
}

function MetricConnectorConnectionCard({
  pin,
  connection,
  authSchemes,
  description,
  connectEnabled,
  connectDisabledReason,
  onConnect,
  onDisconnect,
  onDelete,
  onResync,
  resyncMenuTitle,
  resyncMenuIcon,
  renderConnectionMeta,
  isLoading = false,
  className,
}: ConnectionCardMetricConnectorProps) {
  'use memo';

  const { t } = useTranslation('connection-ui');
  const confirm = useConfirm();
  const [isBusy, setIsBusy] = useState(false);
  const isConnected = connection !== null && connection.status === 'ACTIVE';
  const showDelete = connection !== null && !isConnected;
  const busy = isLoading || isBusy;
  const subtitle = connection !== null && connection.name.trim() !== '' ? connection.name : null;
  const lastSyncedLabel = connection?.lastSyncedAt
    ? t('metricLastSynced', { date: formatDateISO(connection.lastSyncedAt) })
    : null;
  const customMeta = connection && renderConnectionMeta ? renderConnectionMeta(connection) : null;

  const handleDisconnect = async (): Promise<void> => {
    if (!connection) {
      return;
    }
    const { confirmed } = await confirm({
      title: t('disconnect'),
      description: t('disconnectMetricConfirmDescription', { name: pin.name }),
      confirmationButtonProps: { color: 'error' },
    });
    if (!confirmed) {
      return;
    }
    setIsBusy(true);
    try {
      await onDisconnect(connection.id);
    } finally {
      setIsBusy(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!connection) {
      return;
    }
    const { confirmed } = await confirm({
      title: t('delete'),
      description: t('deleteMetricConfirmDescription', { name: pin.name }),
      confirmationButtonProps: { color: 'error' },
    });
    if (!confirmed) {
      return;
    }
    setIsBusy(true);
    try {
      await onDelete(connection.id);
    } finally {
      setIsBusy(false);
    }
  };

  const menuItems: MenuItemType[] = [
    ...(onResync
      ? [
          {
            title: resyncMenuTitle ?? t('metricResync'),
            icon: resyncMenuIcon ?? 'rotate',
            value: 'resync',
            onClick: () => {
              if (connection) {
                void onResync(connection.id);
              }
            },
          } satisfies MenuItemType,
        ]
      : []),
    {
      title: t('disconnect'),
      icon: 'light-puzzle-piece-slash',
      value: 'disconnect',
      onClick: () => {
        void handleDisconnect();
      },
    },
  ];

  return (
    <Card
      className={clsx('metric-connector-connection-card', className)}
      variant="outlined"
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        opacity: isConnected ? 1 : 0.88,
      }}
    >
      <ConnectionCardBody
        toolkitSlug={pin.connectorKey}
        logoNode={<MetricConnectorLogo pin={pin} size={56} />}
        title={pin.name}
        subtitle={subtitle}
        description={connection ? null : (description ?? null)}
        headerAction={
          isConnected && connection ? (
            <DropdownMenu
              className={`metric-connector-card-${connection.id}-dropdown`}
              icon="ellipsis-stroke-vertical"
              title={t('connectionActions')}
              items={menuItems}
              position="left"
              size="medium"
              padding={3}
              loading={busy}
            />
          ) : (
            <PowerIconButton
              icon="plus"
              size="medium"
              padding={3}
              disabled={!connectEnabled || busy}
              onClick={onConnect}
              title={connectDisabledReason ?? t('metricConnect')}
              aria-label={t('metricConnect')}
            />
          )
        }
        stats={
          connection ? (
            <ConnectionStatusChip status={connection.status as ConnectionStatus} />
          ) : (
            <Chip size="small" label={t('metricRecommended')} variant="outlined" />
          )
        }
        meta={
          customMeta ? (
            customMeta
          ) : lastSyncedLabel ? (
            <Typography variant="caption" color="text.secondary">
              {lastSyncedLabel}
            </Typography>
          ) : connectDisabledReason && !isConnected ? (
            <Typography variant="caption" color="text.secondary">
              {connectDisabledReason}
            </Typography>
          ) : null
        }
        authSchemes={authSchemes}
        toolAccessMode={connection?.toolAccessMode ?? null}
        footer={
          showDelete ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                size="small"
                variant="outlined"
                color="error"
                loading={busy}
                startIcon={<FaIconUvilo icon="trash-can" square />}
                onClick={() => {
                  void handleDelete();
                }}
              >
                {t('delete')}
              </Button>
            </Box>
          ) : undefined
        }
      />
    </Card>
  );
}
